import { useState } from 'react';
import { adminApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import Button from '../ui/Button.jsx';
import Input from '../ui/Input.jsx';
import Select from '../ui/Select.jsx';
import { Check, ChevronRight, ChevronLeft, Loader2, Sparkles, Users, Umbrella, CalendarDays, GitBranch, Mail, Rocket, FileText } from 'lucide-react';

const TIMEZONE_OPTIONS = [
  { value: 'Asia/Riyadh', label: 'Asia/Riyadh (UTC+3)' },
  { value: 'Asia/Dubai', label: 'Asia/Dubai (UTC+4)' },
  { value: 'Europe/London', label: 'Europe/London' },
  { value: 'America/New_York', label: 'America/New_York' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles' },
];

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const STEPS = [
  { key: 'welcome', icon: Sparkles, label: 'Welcome' },
  { key: 'company', icon: Sparkles, label: 'Company' },
  { key: 'users', icon: Users, label: 'Users' },
  { key: 'leave', icon: Umbrella, label: 'Leave Types' },
  { key: 'contracts', icon: FileText, label: 'Contracts' },
  { key: 'holidays', icon: CalendarDays, label: 'Holidays' },
  { key: 'chains', icon: GitBranch, label: 'Approvals' },
  { key: 'email', icon: Mail, label: 'Email & Calendar' },
  { key: 'done', icon: Rocket, label: 'Done' },
];

export default function SetupWizard({ onComplete }) {
  const toast = useToast();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({});

  // Company settings form
  const [company, setCompany] = useState({ companyName: '', defaultTimezone: 'Asia/Riyadh', notificationFromEmail: '', appBaseUrl: '' });
  const [workingDays, setWorkingDays] = useState([0, 1, 2, 3, 4]);

  const doAction = async (key, fn) => {
    setLoading(true);
    try {
      const res = await fn();
      setResults((r) => ({ ...r, [key]: res }));
      toast.success(`${key} completed`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const finish = () => {
    try { localStorage.setItem('prohr_setup_done', 'true'); } catch {}
    onComplete();
  };

  const pct = Math.round((step / (STEPS.length - 1)) * 100);
  const current = STEPS[step];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-profun-midnight" style={{ animation: 'fadeIn 300ms ease-out' }}>
      {/* Ambient orbs */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 -right-32 h-[500px] w-[500px] rounded-full bg-profun-blue/10 blur-[100px]" />
        <div className="absolute -bottom-32 -left-32 h-[500px] w-[500px] rounded-full bg-profun-purple/8 blur-[100px]" />
      </div>

      <div className="relative z-10 w-full max-w-lg mx-4">
        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-white/40">Step {step + 1} of {STEPS.length}</span>
            <span className="text-xs text-white/40">{pct}%</span>
          </div>
          <div className="h-1 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-profun-blue rounded-full transition-all duration-300" style={{ width: `${pct}%` }} />
          </div>
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.05] backdrop-blur-md p-8 shadow-2xl">
          {/* ── Welcome ── */}
          {current.key === 'welcome' && (
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-profun-blue/20">
                <img src="/profun-logo.svg" alt="KSA ProHR" className="h-10 w-10" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Welcome to KSA ProHR</h2>
              <p className="text-sm text-white/50 mb-6">Let's set up your organization in a few quick steps. You can always change these later in Settings.</p>
              <Button onClick={next} className="w-full">Get Started <ChevronRight className="h-4 w-4" /></Button>
            </div>
          )}

          {/* ── Company Settings ── */}
          {current.key === 'company' && (
            <div>
              <h2 className="text-lg font-bold text-white mb-1">Company Settings</h2>
              <p className="text-xs text-white/40 mb-4">Basic configuration for your organization</p>
              <div className="space-y-3">
                <Input label="Company Name" value={company.companyName} onChange={(e) => setCompany((c) => ({ ...c, companyName: e.target.value }))} placeholder="e.g. ProFun" />
                <Select label="Timezone" value={company.defaultTimezone} onChange={(e) => setCompany((c) => ({ ...c, defaultTimezone: e.target.value }))} options={TIMEZONE_OPTIONS} />
                <div>
                  <label className="text-[13px] font-medium text-muted-foreground mb-1.5 block">Working Days</label>
                  <div className="flex gap-1.5">
                    {DAY_LABELS.map((d, i) => (
                      <button key={i} onClick={() => setWorkingDays((wd) => wd.includes(i) ? wd.filter((x) => x !== i) : [...wd, i].sort())}
                        className={`rounded-md border px-3 py-1.5 text-xs transition-colors ${workingDays.includes(i) ? 'border-profun-blue bg-profun-blue/20 text-white font-semibold' : 'border-white/10 text-white/40'}`}
                      >{d}</button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex justify-between mt-6">
                <Button variant="ghost" onClick={prev}><ChevronLeft className="h-4 w-4" /> Back</Button>
                <Button onClick={async () => {
                  await doAction('company', () => adminApi.updateSettings({ ...company, defaultWorkingDays: workingDays }));
                  next();
                }} loading={loading}>Save & Continue <ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          )}

          {/* ── Sync Users ── */}
          {current.key === 'users' && (
            <div>
              <h2 className="text-lg font-bold text-white mb-1">Import Users</h2>
              <p className="text-xs text-white/40 mb-4">Pull your organization's users from Microsoft Entra ID</p>
              {results.users ? (
                <div className="rounded-lg bg-profun-teal/10 border border-profun-teal/20 p-4 mb-4">
                  <div className="flex items-center gap-2 text-profun-teal text-sm font-medium"><Check className="h-4 w-4" /> Users synced</div>
                  <p className="text-xs text-white/40 mt-1">{results.users.created} created, {results.users.updated} updated</p>
                </div>
              ) : (
                <Button onClick={() => doAction('users', adminApi.syncUsers)} loading={loading} className="w-full mb-4">
                  {loading ? 'Syncing...' : 'Sync from Microsoft Graph'}
                </Button>
              )}
              <div className="flex justify-between">
                <Button variant="ghost" onClick={prev}><ChevronLeft className="h-4 w-4" /> Back</Button>
                <Button onClick={next}>{results.users ? 'Continue' : 'Skip'} <ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          )}

          {/* ── Leave Types ── */}
          {current.key === 'leave' && (
            <div>
              <h2 className="text-lg font-bold text-white mb-1">Leave Types</h2>
              <p className="text-xs text-white/40 mb-4">Set up the leave types employees can request. We'll seed common defaults.</p>
              {results.leave ? (
                <div className="rounded-lg bg-profun-teal/10 border border-profun-teal/20 p-4 mb-4">
                  <div className="flex items-center gap-2 text-profun-teal text-sm font-medium"><Check className="h-4 w-4" /> Leave types created</div>
                  <p className="text-xs text-white/40 mt-1">{results.leave.leaveTypes?.created || 0} types, {results.leave.templates?.created || 0} email templates</p>
                </div>
              ) : (
                <Button onClick={() => doAction('leave', () => adminApi.seed(false))} loading={loading} className="w-full mb-4">
                  {loading ? 'Seeding...' : 'Seed Default Leave Types & Templates'}
                </Button>
              )}
              <div className="flex justify-between">
                <Button variant="ghost" onClick={prev}><ChevronLeft className="h-4 w-4" /> Back</Button>
                <Button onClick={next}>{results.leave ? 'Continue' : 'Skip'} <ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          )}

          {/* ── Contracts ── */}
          {current.key === 'contracts' && (
            <div>
              <h2 className="text-lg font-bold text-white mb-1">Contracts & Entitlements</h2>
              <p className="text-xs text-white/40 mb-4">
                Each employee needs an annual leave entitlement before they can request leave. After this wizard, go to
                <span className="font-semibold text-white"> Contracts</span> in the sidebar, click any row, and set the entitlement + accrual start date. You can also upload the signed contract PDF.
              </p>
              <div className="rounded-lg bg-white/5 border border-white/10 p-4 mb-4 text-xs text-white/60 space-y-1">
                <p><span className="text-white/80 font-semibold">Tip:</span> The People page will show a <span className="text-amber-400 font-semibold">Needs Setup</span> chip on anyone missing an entitlement — click it to see exactly what is missing.</p>
              </div>
              <div className="flex justify-between">
                <Button variant="ghost" onClick={prev}><ChevronLeft className="h-4 w-4" /> Back</Button>
                <Button onClick={next}>Got it <ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          )}

          {/* ── Holidays ── */}
          {current.key === 'holidays' && (
            <div>
              <h2 className="text-lg font-bold text-white mb-1">Company Holidays</h2>
              <p className="text-xs text-white/40 mb-4">Import public holidays so leave calculations are accurate</p>
              <div className="flex gap-2 mb-4">
                {['KSA_2026', 'US_2026'].map((pack) => (
                  <Button key={pack} variant="secondary" size="sm"
                    onClick={() => doAction('holidays', () => adminApi.importHolidays(pack))}
                    loading={loading} disabled={!!results.holidays}
                  >{pack.replace('_', ' ')}</Button>
                ))}
              </div>
              {results.holidays && (
                <div className="rounded-lg bg-profun-teal/10 border border-profun-teal/20 p-4 mb-4">
                  <div className="flex items-center gap-2 text-profun-teal text-sm font-medium"><Check className="h-4 w-4" /> {results.holidays.imported} holidays imported</div>
                </div>
              )}
              <div className="flex justify-between">
                <Button variant="ghost" onClick={prev}><ChevronLeft className="h-4 w-4" /> Back</Button>
                <Button onClick={next}>{results.holidays ? 'Continue' : 'Skip'} <ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          )}

          {/* ── Approval Chains ── */}
          {current.key === 'chains' && (
            <div>
              <h2 className="text-lg font-bold text-white mb-1">Approval Chains</h2>
              <p className="text-xs text-white/40 mb-4">A default chain (direct manager approval) was created with the seed data. You can customize chains later in the admin panel.</p>
              <div className="rounded-lg bg-white/5 border border-white/10 p-4 mb-4">
                <p className="text-sm text-white/70">Default chain: <span className="font-semibold text-white">Direct Manager → Approved</span></p>
                <p className="text-xs text-white/40 mt-1">Assign users to this chain in People → Edit User → Approval Chain</p>
              </div>
              <div className="flex justify-between">
                <Button variant="ghost" onClick={prev}><ChevronLeft className="h-4 w-4" /> Back</Button>
                <Button onClick={next}>Continue <ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          )}

          {/* ── Email ── */}
          {current.key === 'email' && (
            <div>
              <h2 className="text-lg font-bold text-white mb-1">Email & Calendar</h2>
              <p className="text-xs text-white/40 mb-4">
                This M365 mailbox sends notifications <span className="text-white/70">and</span> hosts the shared team calendar. Approved leave + WFH/Remote days push here so everyone sees them in Outlook within seconds.
              </p>
              <div className="space-y-3">
                <Input label="Notification Sender Email" value={company.notificationFromEmail} onChange={(e) => setCompany((c) => ({ ...c, notificationFromEmail: e.target.value }))} placeholder="e.g. noreply@profun.com" />
                <Input label="App URL (for email links)" value={company.appBaseUrl} onChange={(e) => setCompany((c) => ({ ...c, appBaseUrl: e.target.value }))} placeholder="e.g. https://prohr.profun.com" />
                <p className="text-xs text-muted-foreground">Use the public domain your team actually uses — not the *.azurestaticapps.net default, which breaks sign-in from email links.</p>
              </div>
              <div className="flex justify-between mt-6">
                <Button variant="ghost" onClick={prev}><ChevronLeft className="h-4 w-4" /> Back</Button>
                <Button onClick={async () => {
                  await doAction('email', () => adminApi.updateSettings({ notificationFromEmail: company.notificationFromEmail, appBaseUrl: company.appBaseUrl }));
                  next();
                }} loading={loading}>Save & Finish <ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          )}

          {/* ── Done ── */}
          {current.key === 'done' && (
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-profun-teal/20">
                <Rocket className="h-8 w-8 text-profun-teal" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">You're All Set!</h2>
              <p className="text-sm text-white/50 mb-4">Your organization is configured. Head into the admin panel to keep going.</p>
              <div className="rounded-lg bg-white/5 border border-white/10 p-3 mb-6 text-left text-xs text-white/60 space-y-1">
                <p className="text-white/80 font-semibold text-[13px] mb-1">A few quick patterns to remember:</p>
                <p>• <span className="text-white">Contracts</span> — click any row to edit entitlement</p>
                <p>• <span className="text-white">Balances</span> — click any number to adjust manually</p>
                <p>• <span className="text-white">People</span> — click a "Needs Setup" chip to see what's missing</p>
                <p>• <span className="text-white">Leave Types</span> — trash icon permanently deletes (toggle deactivates)</p>
              </div>
              <Button onClick={finish} className="w-full">Get Started <ChevronRight className="h-4 w-4" /></Button>
            </div>
          )}
        </div>
      </div>

      <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
    </div>
  );
}
